import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';
import './Workflows.css';

/** A small count in words, as the rest of the page writes them; figures past twelve. */
const countWords = (n) => (['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'][n] || String(n));

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * The workflow builder.
 *
 * Gordon, asked which automation rules NCMB wanted: "i have no idea on rules
 * for the time being - i need to see the workflow builder and what variables
 * it has."
 *
 * So this screen's first job is not to collect a rule. It is to SHOW the
 * vocabulary, because nobody can specify automation in the abstract and
 * everybody can recognise one of their own problems in a list of what is
 * possible. Four events, six actions, every setting on every action, every
 * {{variable}}, and - the part a builder usually leaves out - what the engine
 * cannot do, because the most wanted rule in any helpdesk is "if nobody has
 * touched this in four hours" and this engine has no clock.
 *
 * EVERYTHING ON THIS SCREEN COMES FROM /api/workflows/catalogue. Not one
 * action name, field name, status name or variable is typed into this file.
 * The engine's actions live in a registry in lib/workflow-actions.js and the
 * catalogue is generated from it, so an action added later appears here with
 * its form, and an action removed stops being offered. The reason is the
 * endpoint this replaces: /templates/list offered four ready-made rules built
 * on an action that does not exist, an event nothing publishes and a
 * condition language that was never implemented - a frontend list that had
 * drifted from the backend with nothing comparing them.
 *
 * e2e/workflows.mjs reads lib/workflow-actions.js and asserts this screen
 * offers exactly what the registry holds, so the drift cannot come back.
 */

const EMPTY = { name: '', description: '', trigger_event: '', steps: [],
  trigger_config: {} };

/** An empty comparison, ready to be filled in. */
const BLANK = { field: '', op: 'is', value: '' };

/**
 * Read whatever is stored as the one shape this editor draws: a list, and
 * whether all or any of it has to hold.
 *
 * Three things can arrive here. A rule written today is `{all: [...]}` or
 * `{any: [...]}`. A rule written before this screen existed is a flat
 * `{key: value}` object, which lib/conditions.js reads as "every key equals
 * its value" - so it is shown that way rather than silently discarded, which
 * would lose somebody's rule the first time they opened it. And a single
 * bare comparison is a list of one.
 */
function asList(value) {
  if (!value || typeof value !== 'object') return { mode: 'all', list: [] };
  if (Array.isArray(value.all)) return { mode: 'all', list: value.all };
  if (Array.isArray(value.any)) return { mode: 'any', list: value.any };
  if (value.field) return { mode: 'all', list: [value] };
  const keys = Object.keys(value);
  if (keys.length === 0) return { mode: 'all', list: [] };
  return { mode: 'all',
    list: keys.map((k) => ({ field: k, op: 'is', value: value[k] })) };
}

/**
 * The condition editor.
 *
 * Gordon: "Lets add greater than, less than, and, or, etc."
 *
 * WHAT IT DELIBERATELY IS NOT. There is no free-text box and no expression to
 * type. Every comparison is three pickers - a field, an operator, a value -
 * because the thing behind it is a comparison language rather than a
 * scripting one, and a screen that let somebody type an expression would be
 * promising something the engine cannot run.
 *
 * ONE LEVEL OF NESTING, and that is a judgement rather than a limit of the
 * engine, which nests as deep as you like. "All of these" and "any of these"
 * covers every rule a helpdesk has ever asked us for; a tree editor for the
 * handful that need more would make the common case worse for everybody. The
 * line below says so out loud rather than leaving it to be discovered.
 *
 * THE FIELDS COME FROM THE CATALOGUE, in two groups: what this particular
 * event carries, and the ticket itself. The second group is the one that
 * matters - the events mostly carry ids, and "the ticket's priority is
 * Critical" is a rule somebody can read, where "newStatus is 4" is not.
 */
function Conditions({ cat, event, value, canEdit, onChange }) {
  const { mode, list } = asList(value);
  const ops = cat.operators || [];
  const opOf = (name) => ops.find((o) => o.op === name) || ops[0] || {};

  const write = (nextList, nextMode = mode) => {
    if (nextList.length === 0) return onChange({});
    onChange({ [nextMode]: nextList });
  };

  const setAt = (i, patch) => write(list.map((c, n) => (n === i ? { ...c, ...patch } : c)));

  return (
    <div className="wf-conds">
      {list.map((c, i) => {
        const spec = opOf(c.op || 'is');
        return (
          <div className="wf-cond" key={i}>
            {/* and / or, shown on every row but the first, where it would be
                a connective with nothing to connect. Changing it anywhere
                changes the whole group, because the engine has one connective
                per group rather than one per row - saying so by making every
                control the same control is clearer than disabling five of
                them. */}
            {i > 0 && (
              <select className="wf-input wf-join" value={mode} disabled={!canEdit}
                onChange={(e) => write(list, e.target.value)}>
                <option value="all">and</option>
                <option value="any">or</option>
              </select>
            )}
            {i === 0 && <span className="wf-join-pad">if</span>}

            <select className="wf-input" value={c.field || ''} disabled={!canEdit}
              onChange={(e) => setAt(i, { field: e.target.value })}>
              <option value="">Choose what to test…</option>
              {(event?.fields || []).length > 0 && (
                <optgroup label={`What "${event.label}" carries`}>
                  {event.fields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.key}{f.kind === 'id' ? ' (an id)' : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="The ticket itself">
                {(cat.ticketFields || []).map((f) => (
                  <option key={f.key} value={f.key}>{f.key.replace('ticket.', '')}</option>
                ))}
              </optgroup>
            </select>

            <select className="wf-input wf-op" value={c.op || 'is'} disabled={!canEdit}
              onChange={(e) => setAt(i, { op: e.target.value,
                // A comparison that takes nothing must not carry a stale
                // value: the server refuses "is empty" with one, so leaving
                // it behind would make the rule unsaveable for a reason the
                // screen is not showing.
                value: opOf(e.target.value).takes === 'nothing' ? '' : c.value })}>
              {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>

            {spec.takes !== 'nothing' && (
              <input
                className="wf-input"
                type={spec.takes === 'number' ? 'number' : 'text'}
                step={spec.takes === 'number' ? 'any' : undefined}
                value={c.value ?? ''}
                disabled={!canEdit}
                placeholder={spec.takes === 'number' ? '0' : 'a value'}
                onChange={(e) => setAt(i, { value: e.target.value })}
              />
            )}

            <button type="button" className="wf-small wf-del" disabled={!canEdit}
              onClick={() => write(list.filter((_, n) => n !== i))}
              aria-label={`Remove condition ${i + 1}`}>Remove</button>
          </div>
        );
      })}

      {canEdit && (
        <button type="button" className="wf-small"
          onClick={() => write([...list, { ...BLANK }])}>
          {list.length === 0 ? 'Always \u2014 add a condition\u2026' : 'Add another'}
        </button>
      )}

      {list.length > 0 && (
        <p className="wf-help">
          {mode === 'all'
            ? 'Every one of these has to hold.'
            : 'At least one of these has to hold.'}
          {' '}A field marked <em>(an id)</em> carries a number, not a name \u2014
          test the ticket\u2019s own fields below it to compare words.
          Mixing <em>and</em> with <em>or</em> in one step is not offered;
          use a second rule for that.
        </p>
      )}
    </div>
  );
}


const Workflows = ({ canEdit = true }) => {
  const [confirm, confirmDialog] = useConfirm();
  const [cat, setCat] = useState(null);
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState(null);       // null = not editing
  const [editingId, setEditingId] = useState(null);
  const [check, setCheck] = useState({ problems: [], sentence: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState({});           // workflow id -> executions
  const [tried, setTried] = useState(null);
  const [reference, setReference] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/workflows`);
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error
        || 'Could not read the rules');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/workflows/catalogue`);
        setCat(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.response?.data?.error
          || 'Could not read what a rule can do');
      }
      await loadRules();
    })();
  }, [loadRules]);

  const actionsByName = useMemo(
    () => Object.fromEntries((cat?.actions || []).map((a) => [a.action, a])),
    [cat]);

  /*
   * The sentence and the complaints both come from the server, which is the
   * same pair of functions that guard the save. A preview computed here could
   * describe a rule the save then rejects, and the administrator would have no
   * way to tell which of the two was lying.
   */
  useEffect(() => {
    if (!draft) return;
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_BASE}/workflows/validate`, draft);
        if (live) setCheck(res.data);
      } catch { /* the save re-checks; a failed preview is not worth a banner */ }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [draft]);

  const begin = () => {
    setEditingId(null);
    setDraft({ ...EMPTY, trigger_event: cat?.events?.[0]?.event || '' });
    setError(null);
  };

  const open = async (id) => {
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/workflows/${id}`);
      setEditingId(id);
      setDraft({
        name: res.data.name || '',
        description: res.data.description || '',
        trigger_event: res.data.trigger_event || '',
        trigger_config: res.data.trigger_config || {},
        steps: res.data.steps || []
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not open that rule');
    }
  };

  const setStep = (i, patch) => setDraft((d) => ({
    ...d,
    steps: d.steps.map((s, n) => (n === i ? { ...s, ...patch } : s))
  }));

  const setConfig = (i, key, value) => setDraft((d) => ({
    ...d,
    steps: d.steps.map((s, n) => (n === i
      ? { ...s, config: { ...(s.config || {}), [key]: value } } : s))
  }));

  // On a device event the first step a rule needs is "Raise a ticket" -
  // nothing else can run before there is one - so that is what it starts as.
  const addStep = () => setDraft((d) => {
    // A person event (113) starts with no ticket too.
    // So does the licence event (119), and the contract events (120).
    const ev = cat?.events?.find((e) => e.event === d.trigger_event);
    const device = ['device', 'person', 'licence', 'contract'].includes(ev?.about);
    // An event that names its actions (maintenance.raised, wave 6) starts
    // with the first of them.
    const first = Array.isArray(ev?.actions) ? (ev.actions[0] || '')
      : device && !d.steps.some((x) => x.action_type === 'raise_ticket')
        ? 'raise_ticket' : (cat?.actions?.find((a) => a.about !== 'device')?.action || '');
    return {
      ...d,
      steps: [...d.steps, { action_type: first, config: {}, conditions: {}, enabled: true }]
    };
  });

  const removeStep = (i) => setDraft((d) => ({
    ...d, steps: d.steps.filter((_, n) => n !== i)
  }));

  const moveStep = (i, by) => setDraft((d) => {
    const to = i + by;
    if (to < 0 || to >= d.steps.length) return d;
    const steps = [...d.steps];
    [steps[i], steps[to]] = [steps[to], steps[i]];
    return { ...d, steps };
  });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editingId) await axios.put(`${API_BASE}/workflows/${editingId}`, draft);
      else await axios.post(`${API_BASE}/workflows`, draft);
      setDraft(null);
      setEditingId(null);
      await loadRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that rule');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule) => {
    try {
      await axios.put(`${API_BASE}/workflows/${rule.id}/status`,
        { status: rule.status === 'active' ? 'inactive' : 'active' });
      await loadRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change that');
    }
  };

  const remove = async (rule) => {
    if (!(await confirm({ title: `Delete "${rule.name}"?`, message: 'Its history goes with it.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API_BASE}/workflows/${rule.id}`);
      await loadRules();
    } catch (err) {
      // A rule that has run refuses deletion and says why, at length — its
      // runs are the record of what it did to real tickets. The detail is
      // the useful half, so it is not dropped on the floor here.
      const d = err.response?.data;
      setError([d?.error, d?.detail].filter(Boolean).join(' ') || 'Could not delete that');
    }
  };

  const showRuns = async (id) => {
    if (runs[id]) return setRuns((r) => ({ ...r, [id]: undefined }));
    try {
      const res = await axios.get(`${API_BASE}/workflows/${id}/executions`);
      setRuns((r) => ({ ...r, [id]: res.data || [] }));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read what it has done');
    }
  };

  /*
   * Trying a rule on a real ticket. The number is typed rather than picked
   * from a list because this is an administrator's test on a ticket they
   * already have open in the other tab, and the ticket list is thousands
   * long. It really runs - it really changes that ticket - and the button
   * says so.
   */
  const tryIt = async (rule) => {
    const number = await confirm({
      title: `Try "${rule.name}" on which ticket?`,
      message: 'Type a ticket reference. This really runs the rule: it will change '
        + 'that ticket and send any emails the rule sends.',
      prompt: { label: 'Ticket reference', placeholder: 'e.g. JAR-2026-0001', required: true },
      confirmLabel: 'Run it'
    });
    if (!number) return;
    setBusy(true);
    setError(null);
    try {
      const found = await axios.get(`${API_BASE}/tickets`, { params: { search: number.trim() } });
      const ticket = (found.data || []).find(
        (t) => String(t.ticket_number).toLowerCase() === number.trim().toLowerCase());
      if (!ticket) {
        setError(`No ticket called ${number.trim()}`);
        return;
      }
      const res = await axios.post(`${API_BASE}/workflows/${rule.id}/run`,
        { ticket_id: ticket.id });
      setTried({ rule: rule.name, ticket: ticket.ticket_number, ...res.data });
      await loadRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not run it');
    } finally {
      setBusy(false);
    }
  };

  if (error && !cat) return <p className="wf-error">{error}</p>;
  if (!cat) return <p className="wf-quiet">Loading…</p>;
  // The confirm dialog is rendered at the foot of the main return below.

  const chosenEvent = cat.events.find((e) => e.event === draft?.trigger_event);
  /*
   * A rule about a DEVICE (a warranty ending, a device changing hands) is
   * offered "Raise a ticket" and the {{asset.*}} variables; a rule about a
   * ticket is not - it already has a ticket, and has no device of its own to
   * name. The server refuses the mismatch too; this only keeps it off screen.
   */
  //
  // A rule about a PERSON joining or leaving (113) starts the same way: it
  // is offered "Raise a ticket" (about them) and the {{person.*}} variables.
  const aboutDevice = chosenEvent?.about === 'device';
  const aboutPerson = chosenEvent?.about === 'person';
  // And a rule about a LICENCE renewing or ending (119): "Raise a ticket"
  // (for its owner) and the {{licence.*}} variables.
  const aboutLicence = chosenEvent?.about === 'licence';
  // And a rule about a contractor's CONTRACT (120): "Raise a ticket" (for
  // whoever looks after it) and the {{contract.*}} variables.
  const aboutContract = chosenEvent?.about === 'contract';
  // And a rule about a MAINTENANCE plan's date (wave 6): only the actions
  // the event names - an email to an address typed in - and the
  // {{maintenance.*}} variables. The server refuses anything else too.
  const aboutMaintenance = chosenEvent?.about === 'maintenance';
  const offeredActions = Array.isArray(chosenEvent?.actions)
    ? cat.actions.filter((a) => chosenEvent.actions.includes(a.action))
    : cat.actions.filter((a) => aboutDevice || a.about !== 'device'
      || (aboutPerson && (a.alsoAbout || []).includes('person'))
      || (aboutLicence && (a.alsoAbout || []).includes('licence'))
      || (aboutContract && (a.alsoAbout || []).includes('contract')));
  const offeredVariables = aboutMaintenance
    ? (cat.maintenanceVariables || [])
    : aboutContract
    ? [...(cat.contractVariables || []), ...cat.variables]
    : aboutLicence
    ? [...(cat.licenceVariables || []), ...cat.variables]
    : aboutPerson
    ? [...(cat.personVariables || []), ...cat.variables]
    : aboutDevice
    ? [...(cat.deviceVariables || []), ...cat.variables]
    : cat.variables;

  /** One config box, drawn from what the catalogue says the field is. */
  const field = (f, i, step) => {
    const value = step.config?.[f.key] ?? '';
    const options = f.source ? (cat.lists[f.source] || []) : [];
    const id = `wf-${i}-${f.key}`;

    return (
      <div className="wf-field" key={f.key}>
        <label className="wf-label" htmlFor={id}>
          {f.label}{f.required && <span className="wf-req" aria-hidden="true"> *</span>}
        </label>

        {f.type === 'select' && (
          <select id={id} className="wf-input" value={value} disabled={!canEdit}
            onChange={(e) => setConfig(i, f.key, e.target.value)}>
            <option value="">Choose…</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        {/* "Post in a chat group" (wave 7) with no group to post in: say why
            the list is empty rather than offer an empty choice. */}
        {f.source === 'chatGroups' && options.length === 0 && (
          <p className="wf-help">There are no chat groups in use yet - make one in Chat first.</p>
        )}

        {/* The recipient box is a list PLUS anything typed, because "email the
            requester" and "email helpdesk@ncmb.eu" are both ordinary wants and
            a list alone cannot express the second. */}
        {f.type === 'recipient' && (
          <>
            <select id={id} className="wf-input"
              value={options.some((o) => o.value === value) ? value : '__other'}
              disabled={!canEdit}
              onChange={(e) => setConfig(i, f.key,
                e.target.value === '__other' ? '' : e.target.value)}>
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              <option value="__other">An address I type…</option>
            </select>
            {!options.some((o) => o.value === value) && (
              <input className="wf-input" type="email" value={value} disabled={!canEdit}
                placeholder="somebody@ncmb.eu"
                onChange={(e) => setConfig(i, f.key, e.target.value)} />
            )}
          </>
        )}

        {f.type === 'text' && (
          <input id={id} className="wf-input" type="text" value={value} disabled={!canEdit}
            onChange={(e) => setConfig(i, f.key, e.target.value)} />
        )}

        {f.type === 'textarea' && (
          <textarea id={id} className="wf-input wf-area" rows={3} value={value}
            disabled={!canEdit}
            onChange={(e) => setConfig(i, f.key, e.target.value)} />
        )}

        {f.help && <p className="wf-help">{f.help}</p>}

        {/* The variables, next to the box they work in rather than in a
            manual. They are listed where they are usable and nowhere else -
            {{ticket.title}} in a status name would just be stored as that
            text. */}
        {f.vars && (
          <p className="wf-vars">
            {offeredVariables.map((v) => (
              <button key={v.token} type="button" className="wf-var" disabled={!canEdit}
                onClick={() => setConfig(i, f.key, `${value}${v.token}`)}>
                {v.token}
              </button>
            ))}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="wf">
      <div className="wf-head">
        <div>
          <h2 className="wf-title">Rules that run on their own</h2>
          <p className="wf-intro">
            A rule watches for one thing happening — to a ticket or to a
            device — and then does something about it, without anybody
            pressing anything. Every run is kept, and History below each rule
            shows what it did and to what.
          </p>
        </div>
        {canEdit && (
          <button type="button" className="wf-primary" onClick={begin}>
            Write a rule
          </button>
        )}
      </div>

      {error && <p className="wf-error">{error}</p>}

      {/*
        THE REFERENCE. This is the part Gordon asked for: not a form, a list of
        what exists. It is open by default on a Jarvis with no rules, because
        on that screen it is the only useful thing present.
      */}
      <div className="wf-ref">
        <button type="button" className="wf-disclose"
          aria-expanded={reference || rules.length === 0}
          onClick={() => setReference((v) => !v)}>
          {(reference || rules.length === 0) ? '▾' : '▸'} What a rule can watch for, and what it can do
        </button>

        {(reference || rules.length === 0) && (
          <div className="wf-ref-body">
            <h3 className="wf-ref-h">It can watch for four things</h3>
            <p className="wf-help">
              These are the only moments Jarvis announces. Each one carries a
              few details, and those details are the only things a condition
              can test.
            </p>
            {cat.events.map((e) => (
              <div className="wf-card" key={e.event}>
                <p className="wf-card-h">{e.label}</p>
                <p className="wf-help">{e.when}</p>
                <code className="wf-code">{e.event}</code>
                <ul className="wf-fieldlist">
                  {e.fields.map((f) => (
                    <li key={f.key}>
                      <code>{f.key}</code> — {f.what}
                      {f.note && <em className="wf-note"> {f.note}</em>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Counted, not written: the list grows (a seventh, Post in a chat group, came in wave 7). */}
            <h3 className="wf-ref-h">It can do {countWords(cat.actions.length)} things</h3>
            {cat.actions.map((a) => (
              <div className="wf-card" key={a.action}>
                <p className="wf-card-h">{a.label}</p>
                <p className="wf-help">{a.what}</p>
                {a.risky && (
                  <p className="wf-risky" role="note">
                    Approves work with nobody looking at it.
                  </p>
                )}
                <ul className="wf-fieldlist">
                  {a.fields.map((f) => (
                    <li key={f.key}>
                      {f.label}
                      {f.required ? '' : ' (optional)'}
                      {f.vars ? ' — takes variables' : ''}
                    </li>
                  ))}
                </ul>
                {a.quiet && <p className="wf-note">{a.quiet}</p>}
              </div>
            ))}

            <h3 className="wf-ref-h">Variables, in any message or comment</h3>
            <p className="wf-vars">
              {cat.variables.map((v) => (
                <code className="wf-code" key={v.token}>{v.token}</code>
              ))}
            </p>
            <p className="wf-help">
              Filled in when the rule runs. One that cannot be filled is left
              on the page as it is written, rather than coming out blank.
            </p>

            {/*
              What it cannot do, stated rather than left to be deduced from a
              missing option. The rule everybody wants first is a timer, and
              there is no timer.
            */}
            <h3 className="wf-ref-h">What it cannot do yet</h3>
            <ul className="wf-cannot">
              {cat.cannot.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ===== THE FORM ===== */}
      {draft && (
        <div className="wf-form">
          <h3 className="wf-ref-h">{editingId ? 'This rule' : 'A new rule'}</h3>

          <div className="wf-field">
            <label className="wf-label" htmlFor="wf-name">Call it</label>
            <input id="wf-name" className="wf-input" type="text" value={draft.name}
              disabled={!canEdit} placeholder="Acknowledge every new ticket"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>

          <div className="wf-field">
            <label className="wf-label" htmlFor="wf-when">When this happens</label>
            <select id="wf-when" className="wf-input" value={draft.trigger_event}
              disabled={!canEdit}
              onChange={(e) => setDraft({ ...draft, trigger_event: e.target.value })}>
              {cat.events.map((e) => (
                <option key={e.event} value={e.event}>{e.label}</option>
              ))}
            </select>
            {chosenEvent && <p className="wf-help">{chosenEvent.when}</p>}
            {chosenEvent?.note && <p className="wf-note">{chosenEvent.note}</p>}
          </div>

          {/*
            * A TRIGGER WITH A SETTING ON IT, which no other event has.
            * "A ticket stops moving" means nothing until somebody says after
            * how long, and the number belongs to the rule rather than to the
            * firm - four hours for one administrator, three days for another,
            * both about the same ticket.
            *
            * Drawn from the event's own `parameter`, so an event that gains
            * one later gets its box without this file changing.
            */}
          {chosenEvent?.parameter && (
            <div className="wf-field">
              <label className="wf-label" htmlFor="wf-param">
                {chosenEvent.parameter.label}
                {chosenEvent.parameter.required && <span className="wf-req"> *</span>}
              </label>
              <div className="wf-hours">
                <input
                  id="wf-param"
                  className="wf-input"
                  type="number"
                  min="1"
                  step="1"
                  disabled={!canEdit}
                  placeholder={String(chosenEvent.parameter.default ?? '')}
                  value={draft.trigger_config?.[chosenEvent.parameter.key] ?? ''}
                  onChange={(e) => setDraft({
                    ...draft,
                    trigger_config: { ...(draft.trigger_config || {}),
                      [chosenEvent.parameter.key]: e.target.value }
                  })}
                />
                <span className="wf-hours-unit">hours</span>
                {/* Days are how anybody says "three days", and hours are what
                    the engine counts. Offering both spellings of the same
                    number beats making somebody multiply. */}
                {[4, 8, 24, 48, 72, 168].map((h) => (
                  <button key={h} type="button" className="wf-small" disabled={!canEdit}
                    onClick={() => setDraft({
                      ...draft,
                      trigger_config: { ...(draft.trigger_config || {}),
                        [chosenEvent.parameter.key]: h }
                    })}>
                    {h < 24 ? `${h}h` : `${h / 24}d`}
                  </button>
                ))}
              </div>
              {chosenEvent.parameter.help
                && <p className="wf-help">{chosenEvent.parameter.help}</p>}
            </div>
          )}

          {draft.steps.map((step, i) => {
            const spec = actionsByName[step.action_type];
            return (
              <div className="wf-step" key={i}>
                <div className="wf-step-head">
                  <span className="wf-step-n">Step {i + 1}</span>
                  <div className="wf-step-tools">
                    <button type="button" className="wf-small" disabled={!canEdit || i === 0}
                      onClick={() => moveStep(i, -1)} aria-label={`Move step ${i + 1} up`}>↑</button>
                    <button type="button" className="wf-small" disabled={!canEdit || i === draft.steps.length - 1}
                      onClick={() => moveStep(i, 1)} aria-label={`Move step ${i + 1} down`}>↓</button>
                    <button type="button" className="wf-small wf-del" disabled={!canEdit}
                      onClick={() => removeStep(i)} aria-label={`Remove step ${i + 1}`}>Remove</button>
                  </div>
                </div>

                <div className="wf-field">
                  <label className="wf-label" htmlFor={`wf-act-${i}`}>Do this</label>
                  <select id={`wf-act-${i}`} className="wf-input" value={step.action_type}
                    disabled={!canEdit}
                    onChange={(e) => setStep(i, { action_type: e.target.value, config: {} })}>
                    {offeredActions.map((a) => (
                      <option key={a.action} value={a.action}>
                        {a.label}{a.risky ? ' \u2014 approves without a person' : ''}
                      </option>
                    ))}
                  </select>
                  {spec && <p className="wf-help">{spec.what}</p>}
                  {spec?.quiet && <p className="wf-note">{spec.quiet}</p>}
                  {/*
                    * An action that decides whether work happens, with nobody
                    * involved, should not look like one that writes a
                    * comment. Gordon's own words choosing it: "The risky one,
                    * so it would need tight conditions and a visible trail of
                    * what it approved." The trail is built; the tightness is
                    * the condition below, and this is where that is said.
                    */}
                  {spec?.risky && (
                    <p className="wf-risky" role="note">
                      This approves work with nobody looking at it. Put a
                      condition on this step \u2014 without one it approves every
                      ticket the trigger catches.
                    </p>
                  )}
                </div>

                {spec?.fields.map((f) => field(f, i, step))}

                {/*
                  Conditions. Offered as the event's own field names, because a
                  condition on a key the event does not carry is simply false -
                  conditionHolds compares what the trigger sent, and a typo
                  there is a rule that never fires with nothing to show why.
                */}
                <div className="wf-field">
                  <label className="wf-label">Only when</label>
                  <Conditions
                    cat={cat}
                    event={chosenEvent}
                    value={step.conditions}
                    canEdit={canEdit}
                    onChange={(conditions) => setStep(i, { conditions })}
                  />
                </div>
              </div>
            );
          })}

          {canEdit && (
            <button type="button" className="wf-secondary" onClick={addStep}>
              Add a step
            </button>
          )}

          {/* The rule in one sentence, from the server. */}
          <div className="wf-preview">
            <p className="wf-sentence">{check.sentence}</p>
            {check.problems?.length > 0 && (
              <ul className="wf-problems">
                {check.problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
          </div>

          <div className="wf-actions">
            <button type="button" className="wf-primary" onClick={save}
              disabled={!canEdit || busy || check.problems?.length > 0}>
              {busy ? 'Saving…' : editingId ? 'Save it' : 'Create it'}
            </button>
            <button type="button" className="wf-secondary"
              onClick={() => { setDraft(null); setEditingId(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ===== WHAT IT DID WHEN TRIED ===== */}
      {tried && (
        <div className={`wf-tried wf-${tried.status}`}>
          <p className="wf-card-h">
            {tried.rule} on {tried.ticket}: {tried.status}
            {tried.ms != null && <span className="wf-quiet"> · {tried.ms}ms</span>}
          </p>
          {tried.error && <p className="wf-note">{tried.error}</p>}
          <ol className="wf-steplog">
            {(tried.steps || []).map((s, i) => (
              <li key={i}>
                <strong>{s.status}</strong> {s.action || ''}
                {s.why ? ` — ${s.why}` : ''}
                {s.error ? ` — ${s.error}` : ''}
                {s.output ? ` — ${JSON.stringify(s.output)}` : ''}
              </li>
            ))}
          </ol>
          <button type="button" className="wf-small" onClick={() => setTried(null)}>
            Close
          </button>
        </div>
      )}

      {/* ===== THE RULES THERE ARE ===== */}
      {rules.length === 0 ? (
        <p className="wf-quiet">No rules yet.</p>
      ) : (
        <ul className="wf-list">
          {rules.map((r) => (
            <li className="wf-rule" key={r.id}>
              <div className="wf-rule-head">
                <div>
                  <p className="wf-card-h">{r.name}</p>
                  <p className="wf-help">
                    {r.events.join(', ') || 'no trigger'} · {r.step_count} step
                    {r.step_count === 1 ? '' : 's'} ·{' '}
                    {r.run_count > 0
                      ? `${r.run_count} run${r.run_count === 1 ? '' : 's'}, last ${r.last_run_status}`
                      : 'never run'}
                  </p>
                </div>
                <span className={`wf-badge wf-${r.status}`}>{r.status}</span>
              </div>
              <div className="wf-rule-tools">
                <button type="button" className="wf-small" onClick={() => open(r.id)}>
                  {canEdit ? 'Edit' : 'Look at it'}
                </button>
                {canEdit && (
                  <>
                    <button type="button" className="wf-small" onClick={() => toggle(r)}>
                      {r.status === 'active' ? 'Turn off' : 'Turn on'}
                    </button>
                    <button type="button" className="wf-small" disabled={busy}
                      onClick={() => tryIt(r)}>
                      Try it on a ticket
                    </button>
                  </>
                )}
                <button type="button" className="wf-small" onClick={() => showRuns(r.id)}>
                  {runs[r.id] ? 'Hide history' : 'History'}
                </button>
                {canEdit && (
                  <button type="button" className="wf-small wf-del" onClick={() => remove(r)}>
                    Delete
                  </button>
                )}
              </div>

              {runs[r.id] && (
                runs[r.id].length === 0
                  ? <p className="wf-quiet">It has never run.</p>
                  : (
                    <ul className="wf-runs">
                      {runs[r.id].map((x) => (
                        <li key={x.id} className={`wf-run wf-${x.status}`}>
                          <span className="wf-run-h">
                            {x.ticket_number || 'a deleted ticket'} · {x.status} ·{' '}
                            {new Date(x.started_at).toLocaleString('en-GB', { timeZone: 'Europe/Malta' })} · {x.triggered_by}
                          </span>
                          {x.error_message && <span className="wf-note">{x.error_message}</span>}
                          <ol className="wf-steplog">
                            {x.steps.map((s, i) => (
                              <li key={i}>
                                <strong>{s.status}</strong> {s.action_type}
                                {s.error_message ? ` — ${s.error_message}` : ''}
                              </li>
                            ))}
                          </ol>
                        </li>
                      ))}
                    </ul>
                  )
              )}
            </li>
          ))}
        </ul>
      )}
      {confirmDialog}
    </div>
  );
};

export default Workflows;
